import React, { useEffect, useRef, useState } from "react";
import FeatherIcon from "feather-icons-react";
import NotificationPanel from "./NotificationPanel";
import { useNotificationFeed, resolveViewer } from "./useNotificationFeed";
import "./notificationCenter.scss";

/**
 * Bottom-right floating bubble (chat-widget style) with a badge = pending PAD work + unread
 * notifications. Renders nothing for roles that cannot act on PAD items. Mounted once in the
 * vertical layout so it is present on every authenticated page.
 */
const NotificationCenter = () => {
  const [viewer, setViewer] = useState(() => resolveViewer());
  const [open, setOpen] = useState(false);
  const [pulse, setPulse] = useState(false);
  const [lifted, setLifted] = useState(false);
  const lastCount = useRef(null);
  const rootRef = useRef(null);

  // The dev-only "API: DEV ⇄ PROD" pill shares this corner; sit above it when it exists.
  useEffect(() => {
    const check = () => setLifted(!!document.getElementById("__rdc_api_pill_el__"));
    check();
    const t = setTimeout(check, 1500);
    return () => clearTimeout(t);
  }, []);

  // Re-evaluate who is logged in when auth changes (login/logout in this or another tab).
  useEffect(() => {
    const re = () => setViewer(resolveViewer());
    window.addEventListener("storage", re);
    window.addEventListener("rdc-auth-changed", re);
    return () => {
      window.removeEventListener("storage", re);
      window.removeEventListener("rdc-auth-changed", re);
    };
  }, []);

  const feed = useNotificationFeed(!!viewer);
  const total = (feed.pending?.counts?.total || 0) + (feed.notifications?.unread || 0);

  // Bounce the badge once when the count grows.
  useEffect(() => {
    if (lastCount.current !== null && total > lastCount.current) {
      setPulse(true);
      const t = setTimeout(() => setPulse(false), 600);
      return () => clearTimeout(t);
    }
    lastCount.current = total;
    return undefined;
  }, [total]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!viewer) return null;

  const sev = feed.pending?.counts?.danger ? "has-danger" : feed.pending?.counts?.warning ? "has-warning" : "";

  return (
    <div className={`nc-root${lifted ? " nc-lifted" : ""}`} ref={rootRef}>
      {open && (
        <NotificationPanel
          pending={feed.pending}
          notifications={feed.notifications}
          loading={feed.loading}
          error={feed.error}
          lastLoaded={feed.lastLoaded}
          onRefresh={feed.refresh}
          onClose={() => setOpen(false)}
          onMarkRead={feed.markRead}
          onMarkAllRead={feed.markAllRead}
          viewer={viewer}
        />
      )}
      <button
        type="button"
        className={`nc-bubble ${sev}${pulse ? " pulse" : ""}`}
        onClick={() => {
          setOpen((o) => !o);
          if (!open) feed.refresh(false);
        }}
        aria-label={`Notifications: ${total} pending`}
        title={total ? `${feed.pending?.counts?.total || 0} PAD item(s) pending · ${feed.notifications?.unread || 0} unread` : "No pending actions"}
      >
        <FeatherIcon icon={open ? "x" : "bell"} size={22} />
        {total > 0 && <span className="nc-badge">{total > 999 ? "999+" : total}</span>}
      </button>
    </div>
  );
};

export default NotificationCenter;
